export enum PromptFormat {
  MULTIPLE_CHOICE = 'multiple_choice',
  FREE_INPUT = 'free_input',
  SLIDER = 'slider',
  DRAWING = 'drawing',
}

export const MoveDurationByFormat: Record<PromptFormat, number> = {
  [PromptFormat.MULTIPLE_CHOICE]: 32,
  [PromptFormat.FREE_INPUT]: 62,
  [PromptFormat.SLIDER]: 47,
  [PromptFormat.DRAWING]: 9000,
};

export enum GuessType {
  MULTIPLE_SELECTION = '__multiple_selection__',
  FREE_TRUTH = '__free_truth__',
  FREE_LIE = '__free_lie__',
}

export enum MoveType {
  MC_READ_THE_ROOM = 'mc_read_the_room',
  FREE_FACT_OR_FIB = 'free_fact_or_fib',
  SLIDER_ALL_PLAY = 'slider_all_play',
  DRAWN_TOGETHER = 'drawn_together',
}

// Bench players bet whether the active player will get the move right.
export enum SideBetPrediction {
  CORRECT = 'correct',
  WRONG = 'wrong',
}

export const MOVE_POINTS = 2;
export const SIDE_BET_POINTS = 1;

export const isSideBetMoveType = (type: MoveType): boolean => (
  type === MoveType.MC_READ_THE_ROOM || type === MoveType.FREE_FACT_OR_FIB
);

export const isSideBetPrediction = (value: unknown): value is SideBetPrediction => (
  value === SideBetPrediction.CORRECT || value === SideBetPrediction.WRONG
);

// Sub-phases of a Drawn Together move. Absent/DRAWING while players draw and
// pick posters; the host advances to PRESENTING (poster showcase on the big
// screen) and then VOTING (players vote on their phones).
export enum DrawnTogetherPhase {
  DRAWING = 'drawing',
  PRESENTING = 'presenting',
  VOTING = 'voting',
}

// Seconds players get to vote on the posters.
export const DRAWN_TOGETHER_VOTE_DURATION = 45;

export const TIE = 'It was a tie between...';

export const PromptFormatName = {
  [PromptFormat.FREE_INPUT]: 'Fact or Fib',
  [PromptFormat.MULTIPLE_CHOICE]: 'Read the Room',
  [PromptFormat.SLIDER]: 'Group Think',
  [PromptFormat.DRAWING]: 'Drawn Together',
};

// Play order for a mixed game. Read the Room and Fact or Fib are team-vs-team;
// Group Think and Drawn Together are all-play finales.
export const PromptPlayOrder: Record<PromptFormat, number> = {
  [PromptFormat.MULTIPLE_CHOICE]: 0,
  [PromptFormat.FREE_INPUT]: 1,
  [PromptFormat.SLIDER]: 2,
  [PromptFormat.DRAWING]: 3,
};

export const RoundTypes: PromptFormat[] = [
  PromptFormat.MULTIPLE_CHOICE,
  PromptFormat.FREE_INPUT,
  PromptFormat.SLIDER,
  PromptFormat.DRAWING,
];

// How many prompts to pick per round type. Read the Room needs two so each
// team gets a turn; the rest are a single prompt.
export const PromptCountByFormat: Record<PromptFormat, number> = {
  [PromptFormat.MULTIPLE_CHOICE]: 2,
  [PromptFormat.FREE_INPUT]: 1,
  [PromptFormat.SLIDER]: 1,
  [PromptFormat.DRAWING]: 1,
};

export const isPromptFormat = (value: unknown): value is PromptFormat => (
  typeof value === 'string' && (RoundTypes as string[]).includes(value)
);

/**
 * Unique valid round types from a request payload. Returns null when the
 * value is present but empty or contains unknown types. Omitted values should
 * be handled by the caller (default to all types).
 */
export const parseRoundTypes = (raw: unknown): PromptFormat[] | null => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return null;
  }

  const unique: PromptFormat[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!isPromptFormat(item)) {
      return null;
    }
    if (!seen.has(item)) {
      seen.add(item);
      unique.push(item);
    }
  }
  return unique;
};

export const roundTypesFromRequest = (raw: unknown): PromptFormat[] | null => {
  if (raw === undefined) {
    return [...RoundTypes];
  }
  return parseRoundTypes(raw);
};

export enum PromptAnswerUnits {
  USD = 'usd',
  PERCENTAGE = 'percentage',
  NUMBER = 'number',
  MILES = 'miles',
  FEET = 'feet',
  FAHRENHEIT = 'F',
  MPH = 'mph',
  YEARS = 'years',
  DAYS = 'days',
  MINUTES = 'minutes',
  SECONDS = 'seconds',
  GALLONS = 'gallons',
  POUNDS = 'pounds',
  WORDS = 'words',
};

export enum NetErrorType {
  KICKED = 'kicked',
}
